import { forwardRef, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const GAP = 6
const EDGE = 8

/**
 * A popover drawn in document.body but anchored where an absolutely positioned
 * child would sit, so a parent's overflow-hidden or a later row's stacking
 * can't clip it or paint over it. A hidden probe takes `anchorClassName`
 * (e.g. "top-full right-0") inside the nearest positioned parent; the popover
 * lines its right edge up with the probe and opens below it, or above the
 * parent when there's no room below. It follows scrolling and resizing.
 * The forwarded ref is the popover itself, for outside-click checks.
 */
const AnchoredPopover = forwardRef(function AnchoredPopover(
  { anchorClassName = 'top-full right-0', className = '', style, children, ...rest },
  ref,
) {
  const probeRef = useRef(null)
  const popRef = useRef(null)
  const [pos, setPos] = useState(null)

  const setRefs = (el) => {
    popRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) ref.current = el
  }

  useLayoutEffect(() => {
    let frame = 0
    const place = () => {
      const probe = probeRef.current
      const pop = popRef.current
      if (!probe || !pop) return
      const p = probe.getBoundingClientRect()
      const parentTop = probe.offsetParent?.getBoundingClientRect().top ?? p.top
      const w = pop.offsetWidth
      const h = pop.offsetHeight
      const vw = window.innerWidth
      const vh = window.innerHeight
      const below = vh - p.bottom - GAP - EDGE
      const above = parentTop - GAP - EDGE
      const top = h > below && above > below
        ? Math.max(EDGE, parentTop - GAP - h)
        : p.bottom + GAP
      const left = Math.min(Math.max(EDGE, p.right - w), vw - w - EDGE)
      setPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }))
    }
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(place)
    }
    place()
    // Content (e.g. history arriving after "Loading...") changes the height.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null
    if (ro && popRef.current) ro.observe(popRef.current)
    window.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      ro?.disconnect()
      window.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
    }
  }, [])

  return (
    <>
      <span ref={probeRef} aria-hidden="true" className={`pointer-events-none absolute h-0 w-0 ${anchorClassName}`} />
      {createPortal(
        <div
          ref={setRefs}
          className={`fixed z-50 ${className}`}
          style={{ ...style, top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden' }}
          {...rest}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  )
})

export default AnchoredPopover
